import { LeadTimeAlertLevel } from '@models/warehouse.model';

/**
 * Subtract N working days (Mon-Fri) from a date.
 */
export function subtractWorkingDays(date: Date, days: number): Date {
  const result = new Date(date);
  let remaining = days;
  while (remaining > 0) {
    result.setDate(result.getDate() - 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) {
      remaining--;
    }
  }
  return result;
}

/**
 * Count working days (Mon-Fri) between two dates.
 * Returns negative if `from` is after `to` (overdue).
 */
export function workingDaysBetween(from: Date, to: Date): number {
  const start = new Date(from);
  start.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);

  if (start.getTime() === end.getTime()) return 0;

  const forward = start < end;
  let count = 0;
  const current = new Date(start);

  if (forward) {
    while (current < end) {
      current.setDate(current.getDate() + 1);
      const day = current.getDay();
      if (day !== 0 && day !== 6) count++;
    }
    return count;
  } else {
    while (current > end) {
      current.setDate(current.getDate() - 1);
      const day = current.getDay();
      if (day !== 0 && day !== 6) count++;
    }
    return -count;
  }
}

/**
 * Get the lead time alert status for a PLANNED material.
 */
export function getLeadTimeStatus(
  taskStartDate: Date | string,
  leadTimeDays: number,
): {
  alertLevel: LeadTimeAlertLevel | null;
  orderByDate: Date;
  daysUntilDeadline: number;
} {
  const startDate = typeof taskStartDate === 'string'
    ? new Date(taskStartDate)
    : new Date(taskStartDate);

  const orderByDate = subtractWorkingDays(startDate, leadTimeDays);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysUntilDeadline = workingDaysBetween(today, orderByDate);

  let alertLevel: LeadTimeAlertLevel | null = null;
  if (daysUntilDeadline < 0) {
    alertLevel = 'OVERDUE';
  } else if (daysUntilDeadline <= 3) {
    alertLevel = 'URGENT';
  } else if (daysUntilDeadline <= 7) {
    alertLevel = 'UPCOMING';
  }

  return { alertLevel, orderByDate, daysUntilDeadline };
}

/**
 * Get Tailwind color classes for an alert level.
 */
export function getAlertColors(level: LeadTimeAlertLevel): {
  bg: string;
  text: string;
  border: string;
} {
  switch (level) {
    case 'OVERDUE':
      return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-500' };
    case 'URGENT':
      return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-500' };
    case 'UPCOMING':
      return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500' };
  }
}
